import React, { useState, useEffect } from "react";
import { Table, Button, Row, Col, Dropdown, OverlayTrigger, Tooltip } from "react-bootstrap";
import { MdKeyboardArrowUp, MdKeyboardArrowDown } from "react-icons/md";
import { RxCaretSort } from "react-icons/rx";
import { BsEye, BsThreeDotsVertical, BsTrash3 } from "react-icons/bs";
import { GoCircleSlash } from "react-icons/go";
import { PiToggleRight } from "react-icons/pi";
import ImageNoDataFound from "../../images/nodata_found.gif";
import ReactPaginate from "react-paginate";
import FilterBlock from "./FilterBlock";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { StatusChangeModal, DeleteConfirmModal } from "./ClientModals";
import apiConfig from "../../pages/credentials/apiConfig";
import "./index.scss";

const AllClientsTable = ({ isClientModalOpen, setCounts, onEditClick, isChecked }) => {
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });
  const [currentPage, setCurrentPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [activeFilters, setActiveFilters] = useState({});


  const itemsPerPage = 10;
  const navigate = useNavigate();

  const fetchClients = async (filters = {}, page = 0, sort = sortConfig) => {
    const authToken = localStorage.getItem("authToken");
    try {
      setLoading(true);

      const safeSorting =
        sort?.key && (sort.direction === "asc" || sort.direction === "desc")
          ? { key: sort.key, order: sort.direction === "asc" ? 1 : -1 }
          : { key: "createdAt", order: -1 };

      const safeFilters = {
        search: filters.search || "",
        industry:
          filters.industry && filters.industry !== "All Industries"
            ? [filters.industry]
            : [],
        clientStatus:
          filters.status && filters.status !== "Status"
            ? [filters.status]
            : [],
        yearEnd: filters.yearEnd || [],
      };

      const response = await axios.post(
        `${apiConfig.apiBaseURL}/getAllClientsListWithStats`,
        {
          page: page + 1,
          perPage: itemsPerPage,
          sorting: safeSorting,
          searchString: filters.search || "",
          filters: safeFilters,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
          },
        }
      );

      const result = response?.data?.data?.[0] || {};
      const docs = result.docs || [];
      const count = result.totalCount?.[0]?.count || 0;

      const formatted = docs.map((client) => ({
        id: client._id,
        clientName: client.clientName || "--",
        contactPerson: client.contactInformation?.[0]?.name || "--",
        industry: client.industry || "--",
       latestYearEnd: client.latestEngagement?.engagementYear || "--",
        status: client.isActive ? "Active" : "Inactive",
      }));

      setData(formatted);
      setFilteredData(formatted);
      setTotalCount(count);

      if (setCounts) {
        setCounts({
          totalClients: result?.totalCount?.[0]?.count || 0,
          activeEngagements: result?.activeEngagements?.[0]?.count || 0,
          upcomingYearEnds: result?.upcomingYearEnds?.[0]?.count || 0,
        });
      }

    } catch (error) {
      console.error("Fetch error:", error);
      toast.error(error?.response?.data?.message || "Failed to load clients.");
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    fetchClients();
  }, [isClientModalOpen, isChecked]);

  const getSortIcon = (key) => {
    if (sortConfig.key === key) {
      return sortConfig.direction === "asc" ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />;
    }
    return <RxCaretSort size={20} />;
  };

  const sortTable = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    const newSort = { key, direction };
    setSortConfig(newSort);
    fetchClients({}, currentPage, newSort);
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    fetchClients(activeFilters, selected, sortConfig);
  };


  const handleFilterApply = (filters) => {
    setSearchValue(filters.search || "");
    setActiveFilters(filters);
    setCurrentPage(0);
    fetchClients(filters, 0, sortConfig);
  };



  const handleClientClick = (clientId) => {
    navigate(`/clients/client-details/${clientId}`);
  };

  const handleStatusChange = (client) => {
    setSelectedClient(client);
    setShowStatusModal(true);
  };

  const handleDeleteClick = (client) => {
    setSelectedClient(client);
    setShowDeleteModal(true);
  };

  const confirmStatusChange = async () => {
    const authToken = localStorage.getItem("authToken");
    setIsActionLoading(true);
    try {
      const newStatus = selectedClient.status === "Active" ? false : true;

      await axios.put(
        `${apiConfig.apiBaseURL}/deactivateClient/${selectedClient.id}`,
        { isActive: newStatus },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(
        `Client ${newStatus ? "activated" : "deactivated"} successfully.`
      );

      fetchClients(activeFilters, currentPage, sortConfig);
    } catch (error) {
      console.error("Status change error:", error);
      toast.error(
        error?.response?.data?.message || "Failed to update client status."
      );
    } finally {
      setShowStatusModal(false);
      setIsActionLoading(false);
    }
  };



  const confirmDelete = async () => {
    const authToken = localStorage.getItem("authToken");
    setIsActionLoading(true);
    try {
      await axios.put(
        `${apiConfig.apiBaseURL}/deleteClient/${selectedClient.id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "x-api-key": apiConfig["x-api-key"],
            "Content-Type": "application/json",
          },
        }
      );

      toast.success("Client deleted successfully.");

      fetchClients(activeFilters, currentPage, sortConfig);

    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error?.response?.data?.message || "Failed to delete client.");
    } finally {
      setShowDeleteModal(false);
      setIsActionLoading(false);
    }
  };


  const getStatusBadge = (status) => (
    <span className="badge rounded px-3 py-2"
      style={{
        backgroundColor: status === "Active" ? "#D9FBEC" : "#FFEDED",
        color: status === "Active" ? "#08A45D" : "#FF0000",
        fontWeight: "700"
      }}>
      {status}
    </span>
  );

  // if (loading) return <div className="text-center py-5">Loading...</div>;

  const displayedData = data;

  return (
    <>
      <StatusChangeModal
        show={showStatusModal}
        onHide={() => setShowStatusModal(false)}
        client={selectedClient}
        onConfirm={confirmStatusChange}
        isLoading={isActionLoading}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        client={selectedClient}
        onConfirm={confirmDelete}
        isLoading={isActionLoading}
      />

      <FilterBlock
        onApply={handleFilterApply}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
      />



      <Table hover responsive style={{ border: "2px solid #E7ECF1" }} className="all-clients-table">
        <thead>
          <tr style={{ whiteSpace: "nowrap" }}>
            <th onClick={() => sortTable("clientName")} className="text-light py-3">
              Client {getSortIcon("clientName")}
            </th>
            <th onClick={() => sortTable("industry")} className="text-light py-3">
              Industry {getSortIcon("industry")}
            </th>
            <th onClick={() => sortTable("latestYearEnd")} className="text-light py-3">
              Latest Year End {getSortIcon("latestYearEnd")}
            </th>
            <th onClick={() => sortTable("status")} className="text-light py-3">
              Status {getSortIcon("status")}
            </th>
            <th className="text-light py-3">Actions</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr style={{ whiteSpace: "nowrap" }}>
              <td colSpan="5" className="text-center align-middle">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </td>
            </tr>
          ) : displayedData.length > 0 ? (
            displayedData.map((row) => (
              <tr key={row.id} style={{ whiteSpace: "nowrap" }}>
                <td className="align-middle">
                  <div
                    onClick={() => handleClientClick(row.id)}
                    style={{
                      color: "#073E72",
                      fontWeight: "700",
                      cursor: "pointer",
                      textDecoration: "none"
                    }}
                    className="client-name-link"
                  >
                    {row.clientName}
                  </div>
                  <div style={{ color: "#6B7280", fontSize: "0.875rem" }}>{row.contactPerson}</div>
                </td>
                <td className="align-middle" style={{ color: "#6B7280" }}>{row.industry}</td>
                <td className="align-middle" style={{ color: "#6B7280" }}>{row.latestYearEnd}</td>
                <td className="align-middle">{getStatusBadge(row.status)}</td>
                {/* <td className="align-middle px-4">
                  <div className="d-flex">
                    <Button
                      variant="link"
                      className="p-1 me-2 bg-transparent"
                      onClick={() => handleClientClick(row.id)}
                    >
                      <BsEye size={25} color="#6B7280" />
                    </Button>
                    <Dropdown>
                      <Dropdown.Toggle as="div" className="cursor-pointer p-1">
                        <BsThreeDotsVertical size={18} color="#6B7280" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleDeleteClick(row)} className="text-light fw-bold">
                          <BsTrash3 className="me-1 text-light" /> Delete
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item onClick={() => handleStatusChange(row)} className="text-light fw-bold">
                          <GoCircleSlash className="me-1 text-light" /> {row.status === "Active" ? "Inactive" : "Activate"}
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </td> */}

                <td className="align-middle ">
                  <div className="d-flex">
                    {/* View Client Tooltip */}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-view-${row.id}`}>View Client</Tooltip>}
                    >
                      <Button
                        variant="link"
                        className="p-1 me-2 bg-transparent"
                        onClick={() => handleClientClick(row.id)}
                      >
                        <BsEye size={25} color="#6B7280" />
                      </Button>
                    </OverlayTrigger>

                    {/* More Actions Tooltip */}
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id={`tooltip-more-${row.id}`}>More Actions</Tooltip>}
                    >
                      <Dropdown className="client-more-actions">
                        <Dropdown.Toggle as="div" className="cursor-pointer p-1">
                          <BsThreeDotsVertical size={18} color="#6B7280" style={{ cursor: 'pointer' }} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="border-0 shadow">
                          <Dropdown.Item
                            onClick={() => handleDeleteClick(row)}
                            className="text-light fw-bold"
                          >
                            <BsTrash3 className="me-1 text-light" /> Delete
                          </Dropdown.Item>
                          {/* <Dropdown.Divider /> */}
                          <Dropdown.Item
                            onClick={() => handleStatusChange(row)}
                            className="text-light fw-bold"
                          >
                            {row.status === "Active" ? (
                              <>
                                <GoCircleSlash className="me-1 text-light" />
                                Inactivate
                              </>
                            ) : (
                              <>
                                <PiToggleRight size={20} className="me-1 text-light" />
                                Activate
                              </>
                            )}
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown>
                    </OverlayTrigger>
                  </div>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="text-center py-5">
                <div>
                  <img src={ImageNoDataFound} alt="No data found" style={{ maxHeight: "150px" }} />
                  <p className="fw-bold text-dark mb-0 mt-3">No data found</p>
                </div>
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      {filteredData.length > 0 && (
        <Row className="mt-3 mb-4">
          <Col md={6}>
            <p style={{ color: "#6B7280" }}>
              Showing: {totalCount === 0 ? 0 : currentPage * itemsPerPage + 1} – {Math.min((currentPage + 1) * itemsPerPage, totalCount)} of {totalCount}
            </p>
          </Col>

          <Col md={6} className="d-flex justify-content-end">
            <ReactPaginate
              previousLabel={"<"}
              nextLabel={">"}
              breakLabel="..."
              pageCount={Math.ceil(totalCount / itemsPerPage)}
              marginPagesDisplayed={2}
              pageRangeDisplayed={2}
              onPageChange={handlePageClick}
              containerClassName={"pagination"}
              activeClassName={"active"}
              forcePage={currentPage}
            />
          </Col>
        </Row>
      )}
      {/* <ToastContainer /> */}
    </>
  );
};

export default AllClientsTable;